import React, { SyntheticEvent } from "react";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import { RootState } from "../../../app/store";
import { useTranslation } from "react-i18next";
import { set } from "../../../app/globalSlice";
import { searchParamToObject } from "../../../helpers/SearchParamHelper";
import { objectToQuery, requestApi } from "../../../helpers/RequestApi";
import { GET, PATCH } from "../../../utils/MethodUtils";
import {
  BANK_TRANSACTION_URL,
  FCOMPTET_URL,
  FDOCENTETES_URL,
} from "../../../utils/UrlsUtils";
import { toastr } from "react-redux-toastr";
import { useSearchParams } from "react-router-dom";
import { DataGrid, GridColDef, GridRenderCellParams } from "@mui/x-data-grid";
import IconButton from "@mui/material/IconButton";
import { getLocaleDataGrid } from "../../../helpers/GetLanguage";
import { GridSortModel } from "@mui/x-data-grid/models/gridSortModel";
import CopyClipboardComponent from "../CopyClipboardComponent";
import FilterSearchComponent from "../filter/FilterSearchComponent";
import { BankTransaction } from "../../../interfaces/BankTransferInterface";
import { priceFormat } from "../../../utils/FormatUtils";
import CircleIcon from "@mui/icons-material/Circle";
import { useTheme } from "@mui/material/styles";
import Tooltip from "@mui/material/Tooltip";
import { blue, green, grey, red, yellow } from "@mui/material/colors";
import EditIcon from "@mui/icons-material/Edit";
import DialogContent from "@mui/material/DialogContent";
import DialogActions from "@mui/material/DialogActions";
import { LoadingButton } from "@mui/lab";
import Dialog from "@mui/material/Dialog";
import {
  Autocomplete,
  Chip,
  CircularProgress,
  DialogProps,
  List,
  ListItem,
  TextField,
} from "@mui/material";
import useMediaQuery from "@mui/material/useMediaQuery";
import Box from "@mui/material/Box";
import { UserInterface } from "../../../interfaces/UserInterface";
import { InputInterface } from "../../../interfaces/InputInterface";
import getErrorApi from "../../../helpers/GetErrorApi";
import Typography from "@mui/material/Typography";
import Divider from "@mui/material/Divider";
import { FDocenteteInterface } from "../../../interfaces/FDocenteteInterface";

interface State {
  bankTransaction: BankTransaction;
}

interface FormState {
  user: InputInterface;
}

interface FormState2 {
  fDocentete: InputInterface;
}

const QontoFDocenteteComponent: React.FC<State> = React.memo(
  ({ bankTransaction }) => {
    const [edit, setEdit] = React.useState(false);
    const [maxWidth] = React.useState<DialogProps["maxWidth"]>("md");
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [searchFDocentete, setSearchFDocentete] = React.useState<string>("");
    const [fDocentetes, setFDocentetes] =
      React.useState<FDocenteteInterface[]>();
    const [isTyping, setIsTyping] = React.useState<boolean>(false);
    const [isSearching, setIsSearching] = React.useState<boolean>(false);
    const token = useAppSelector((state: RootState) => state.globalState.token);
    const dispatch = useAppDispatch();

    const getDefaultValue = React.useCallback((): FormState2 => {
      return {
        fDocentete: {
          value: null,
          error: "",
        },
      };
    }, []);
    const [values, setValues] = React.useState<FormState2>(getDefaultValue());
    const handleClose = React.useCallback(() => {
      setEdit(false);
    }, []);
    const openDialog = React.useCallback(() => {
      setEdit(true);
    }, []);
    const apply = React.useCallback(
      async (doPiece: string | null | undefined = null) => {
        setLoading(true);
        const response = await requestApi({
          method: PATCH,
          path: BANK_TRANSACTION_URL + "/" + bankTransaction.id,
          allowError: false,
          token: token,
          body: {
            doPiece: doPiece ?? values.fDocentete.value.doPiece,
          },
        });
        if (response.statusCode === 200) {
          toastr.success(t("word.success"), "");
          handleClose();
          dispatch(set({ refreshPage: true }));
        } else if (response.statusCode === 401) {
          toastr.info(t("word.info"), t("error.reconnect"));
        } else {
          for (let message of getErrorApi(response.content)) {
            toastr.error(t("word.error"), t(message));
          }
        }
        setLoading(false);
      },
      [bankTransaction.id, dispatch, handleClose, t, token, values.fDocentete.value?.doPiece]
    );

    const handleSearchFDocentete = React.useCallback(
      (event: SyntheticEvent<Element, Event>, value: string) => {
        setSearchFDocentete(value);
        setFDocentetes([]);
        setIsTyping(true);
      },
      []
    );

    const searchFDocentetes = React.useCallback(async () => {
      if (!edit) {
        return;
      }
      setIsTyping(false);
      setIsSearching(true);
      const param: any = {
        doPiece: searchFDocentete.trim().toUpperCase(),
      };
      const response = await requestApi({
        method: GET,
        path: FDOCENTETES_URL + objectToQuery(param),
        allowError: false,
        paginate: false,
        token: token,
        timeout: 30_000,
      });
      if (response.statusCode === 200) {
        setFDocentetes(response.content.fDocentetes);
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setIsSearching(false);
    }, [edit, searchFDocentete, t, token]);

    const handleChangeAutocomplete = React.useCallback(
      (prop: keyof FormState2) =>
        (event: SyntheticEvent<Element, Event>, value: any) => {
          setValues((v) => {
            return {
              ...v,
              // @ts-ignore
              [prop]: { ...v[prop], value: value, error: "" },
            };
          });
        },
      []
    );

    React.useEffect(() => {
      const timeoutTyping = setTimeout(() => {
        searchFDocentetes();
      }, 500);
      return () => clearTimeout(timeoutTyping);
    }, [searchFDocentetes]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
      searchFDocentetes();
    }, [edit]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <>
        {bankTransaction.side === "credit" && (
          <>
            {bankTransaction.doPiece ? (
              <Box>
                <CopyClipboardComponent
                  className="RobotoMono"
                  component="span"
                  text={bankTransaction.doPiece ?? ""}
                />
                {bankTransaction.doPiece && bankTransaction.new && (
                  <LoadingButton
                    variant="contained"
                    loading={loading}
                    onClick={() => apply(bankTransaction.doPiece)}
                  >
                    {t("word.link.action")}
                  </LoadingButton>
                )}
              </Box>
            ) : (
              <>
                <Dialog
                  maxWidth={maxWidth}
                  fullScreen={fullScreen}
                  onClose={handleClose}
                  open={edit}
                >
                  <DialogContent>
                    <Typography>
                      Veuillez indiquer l'utilisateur pour à attribuer la
                      transaction
                    </Typography>
                    <pre>{bankTransaction.reference}</pre>
                    {bankTransaction.expectedDoPiece &&
                      bankTransaction.expectedDoPiece.length > 0 && (
                        <>
                          <Divider sx={{ marginY: 2 }} />
                          <List>
                            {bankTransaction.expectedDoPiece?.map((doPiece) => (
                              <ListItem disablePadding key={doPiece.toString()}>
                                <CopyClipboardComponent
                                  className="RobotoMono"
                                  component="span"
                                  text={doPiece.toString()}
                                />
                              </ListItem>
                            ))}
                          </List>
                        </>
                      )}
                    <Divider sx={{ marginY: 2 }} />
                    <Autocomplete
                      fullWidth
                      loadingText={t("word.loading") + "..."}
                      inputValue={searchFDocentete}
                      onInputChange={handleSearchFDocentete}
                      noOptionsText={
                        isSearching ? (
                          <Box sx={{ textAlign: "center" }}>
                            <CircularProgress />
                          </Box>
                        ) : searchFDocentete !== "" && !isTyping ? (
                          t("sentence.no_option")
                        ) : (
                          t("word.sav.searchFDocentete.label")
                        )
                      }
                      options={fDocentetes ?? []}
                      getOptionLabel={(fDocentete: FDocenteteInterface) => {
                        return fDocentete.doPiece ?? "";
                      }}
                      isOptionEqualToValue={(
                        option: FDocenteteInterface,
                        value: FDocenteteInterface
                      ) => {
                        return option.doPiece === value.doPiece;
                      }}
                      renderOption={(props, option) => {
                        return (
                          <li
                            {...props}
                            key={option.doPiece}
                            style={{ display: "block" }}
                          >
                            <span style={{ color: theme.palette.primary.main }}>
                              {"[" + option.doPiece + "] "}
                            </span>
                            {option.doTiers?.ctNum}
                          </li>
                        );
                      }}
                      onChange={handleChangeAutocomplete("fDocentete")}
                      value={values.fDocentete.value}
                      defaultValue={values.fDocentete.value}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          autoComplete="off"
                          error={!!values.fDocentete.error}
                          helperText={t(values.fDocentete.error ?? "")}
                          label={t("word.doPiece")}
                          placeholder={t("word.doPiece")}
                        />
                      )}
                    />
                  </DialogContent>
                  <DialogActions sx={{ justifyContent: "space-between" }}>
                    <LoadingButton disabled={loading} onClick={handleClose}>
                      {t("word.no")}
                    </LoadingButton>
                    <LoadingButton
                      variant="contained"
                      loading={loading}
                      onClick={() => apply()}
                    >
                      {t("word.apply")}
                    </LoadingButton>
                  </DialogActions>
                </Dialog>
                {bankTransaction.new && (
                  <>
                    <IconButton size="large" onClick={openDialog}>
                      <EditIcon />
                    </IconButton>
                    <Chip
                      label={bankTransaction.expectedDoPiece?.length ?? 0}
                      variant="outlined"
                      color="primary"
                      sx={{ marginLeft: 1 }}
                    />
                  </>
                )}
              </>
            )}
          </>
        )}
      </>
    );
  }
);

const QontoCtNumComponent: React.FC<State> = React.memo(
  ({ bankTransaction }) => {
    const [edit, setEdit] = React.useState(false);
    const [maxWidth] = React.useState<DialogProps["maxWidth"]>("md");
    const theme = useTheme();
    const fullScreen = useMediaQuery(theme.breakpoints.down("md"));
    const { t } = useTranslation();
    const [loading, setLoading] = React.useState<boolean>(false);
    const [searchUser, setSearchUser] = React.useState<string>("");
    const [users, setUsers] = React.useState<UserInterface[]>();
    const [isTyping, setIsTyping] = React.useState<boolean>(false);
    const [isSearching, setIsSearching] = React.useState<boolean>(false);
    const token = useAppSelector((state: RootState) => state.globalState.token);
    const dispatch = useAppDispatch();

    const getDefaultValue = React.useCallback((): FormState => {
      return {
        user: {
          value: null,
          error: "",
        },
      };
    }, []);
    const [values, setValues] = React.useState<FormState>(getDefaultValue());
    const handleClose = React.useCallback(() => {
      setEdit(false);
    }, []);
    const openDialog = React.useCallback(() => {
      setEdit(true);
    }, []);
    const apply = React.useCallback(async (ctNum: string | null | undefined = null) => {
      setLoading(true);
      const response = await requestApi({
        method: PATCH,
        path: BANK_TRANSACTION_URL + "/" + bankTransaction.id,
        allowError: false,
        token: token,
        body: {
          ctNum: ctNum ?? values.user.value.userIdentifier,
          doPiece: null,
        },
      });
      if (response.statusCode === 200) {
        toastr.success(t("word.success"), "");
        handleClose();
        dispatch(set({ refreshPage: true }));
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setLoading(false);
    }, [
      bankTransaction.id,
      dispatch,
      handleClose,
      t,
      token,
      values.user.value?.userIdentifier,
    ]);

    const handleSearchUser = React.useCallback(
      (event: SyntheticEvent<Element, Event>, value: string) => {
        setSearchUser(value);
        setUsers([]);
        setIsTyping(true);
      },
      []
    );

    const searchUsers = React.useCallback(async () => {
      if (!edit) {
        return;
      }
      setIsTyping(false);
      setIsSearching(true);
      const param: any = {
        ctNum: searchUser.trim().toUpperCase(),
      };
      const response = await requestApi({
        method: GET,
        path: FCOMPTET_URL + objectToQuery(param),
        allowError: false,
        paginate: false,
        token: token,
        timeout: 30_000,
      });
      if (response.statusCode === 200) {
        setUsers(response.content);
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setIsSearching(false);
    }, [edit, searchUser, t, token]);

    const handleChangeAutocomplete = React.useCallback(
      (prop: keyof FormState) =>
        (event: SyntheticEvent<Element, Event>, value: any) => {
          setValues((v) => {
            return {
              ...v,
              // @ts-ignore
              [prop]: { ...v[prop], value: value, error: "" },
            };
          });
        },
      []
    );

    React.useEffect(() => {
      const timeoutTyping = setTimeout(() => {
        searchUsers();
      }, 500);
      return () => clearTimeout(timeoutTyping);
    }, [searchUsers]); // eslint-disable-line react-hooks/exhaustive-deps

    React.useEffect(() => {
      searchUsers();
    }, [edit]); // eslint-disable-line react-hooks/exhaustive-deps

    return (
      <>
        <>
          {bankTransaction.side === "credit" && (
            <>
              {bankTransaction.ctNum ? (
                <Box>
                  <CopyClipboardComponent
                    className="RobotoMono"
                    component="span"
                    text={bankTransaction.ctNum ?? ""}
                  />
                  {bankTransaction.new && (
                    <LoadingButton
                      variant="contained"
                      loading={loading}
                      onClick={() => apply(bankTransaction.ctNum)}
                    >
                      {t("word.link.action")}
                    </LoadingButton>
                  )}
                </Box>
              ) : (
                <>
                  <Dialog
                    maxWidth={maxWidth}
                    fullScreen={fullScreen}
                    onClose={handleClose}
                    open={edit}
                  >
                    <DialogContent>
                      <Typography>
                        Veuillez indiquer l'utilisateur pour à attribuer la
                        transaction
                      </Typography>
                      <pre>{bankTransaction.reference}</pre>
                      {bankTransaction.expectedCtNum &&
                        bankTransaction.expectedCtNum.length > 0 && (
                          <>
                            <Divider sx={{ marginY: 2 }} />
                            <List>
                              {bankTransaction.expectedCtNum?.map((ctNum) => (
                                <ListItem disablePadding key={ctNum.toString()}>
                                  <CopyClipboardComponent
                                    className="RobotoMono"
                                    component="span"
                                    text={ctNum.toString()}
                                  />
                                </ListItem>
                              ))}
                            </List>
                          </>
                        )}
                      <Divider sx={{ marginY: 2 }} />
                      <Autocomplete
                        fullWidth
                        loadingText={t("word.loading") + "..."}
                        inputValue={searchUser}
                        onInputChange={handleSearchUser}
                        noOptionsText={
                          isSearching ? (
                            <Box sx={{ textAlign: "center" }}>
                              <CircularProgress />
                            </Box>
                          ) : searchUser !== "" && !isTyping ? (
                            t("sentence.no_option")
                          ) : (
                            t("word.sav.searchUser.label")
                          )
                        }
                        options={users ?? []}
                        getOptionLabel={(user: UserInterface) => {
                          return user.userIdentifier ?? "";
                        }}
                        isOptionEqualToValue={(
                          option: UserInterface,
                          value: UserInterface
                        ) => {
                          return option.userIdentifier === value.userIdentifier;
                        }}
                        renderOption={(props, option) => {
                          return (
                            <li
                              {...props}
                              key={option.userIdentifier}
                              style={{ display: "block" }}
                            >
                              <span
                                style={{ color: theme.palette.primary.main }}
                              >
                                {"[" + option.userIdentifier + "] "}
                              </span>
                              {option.ctIntitule}
                            </li>
                          );
                        }}
                        onChange={handleChangeAutocomplete("user")}
                        value={values.user.value}
                        defaultValue={values.user.value}
                        renderInput={(params) => (
                          <TextField
                            {...params}
                            autoComplete="off"
                            error={!!values.user.error}
                            helperText={t(values.user.error ?? "")}
                            label={t("word.userIdentifier")}
                            placeholder={t("word.userIdentifier")}
                          />
                        )}
                      />
                    </DialogContent>
                    <DialogActions sx={{ justifyContent: "space-between" }}>
                      <LoadingButton disabled={loading} onClick={handleClose}>
                        {t("word.no")}
                      </LoadingButton>
                      <LoadingButton
                        variant="contained"
                        loading={loading}
                        onClick={() => apply()}
                      >
                        {t("word.apply")}
                      </LoadingButton>
                    </DialogActions>
                  </Dialog>
                  {bankTransaction.new && (
                    <>
                      <IconButton size="large" onClick={openDialog}>
                        <EditIcon />
                      </IconButton>
                      <Chip
                        label={bankTransaction.expectedCtNum?.length ?? 0}
                        variant="outlined"
                        color="primary"
                        sx={{ marginLeft: 1 }}
                      />
                    </>
                  )}
                </>
              )}
            </>
          )}
        </>
      </>
    );
  }
);

const QontoStatusComponent: React.FC<State> = React.memo(
  ({ bankTransaction }) => {
    const [loading, setLoading] = React.useState<boolean>(false);
    const token = useAppSelector((state: RootState) => state.globalState.token);
    const { t } = useTranslation();
    const dispatch = useAppDispatch();
    const theme = useTheme();

    const apply = React.useCallback(async () => {
      setLoading(true);
      const response = await requestApi({
        method: PATCH,
        path: BANK_TRANSACTION_URL + "/" + bankTransaction.id,
        allowError: false,
        token: token,
        body: {
          new: false,
        },
      });
      if (response.statusCode === 200) {
        toastr.success(t("word.success"), "");
        dispatch(set({ refreshPage: true }));
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        for (let message of getErrorApi(response.content)) {
          toastr.error(t("word.error"), t(message));
        }
      }
      setLoading(false);
    }, [bankTransaction.id, dispatch, t, token]);

    const getStatusConfig = (row: any) => {
      if (row.errors) {
        return {
          color: red[500],
          title: JSON.stringify(row.errors),
        };
      }
      if (!row.new && !row.rgNo) {
        return {
          color: blue[700],
          title: "La transaction a été marqué comme effectuée manuellement",
        };
      }
      if (row.ctNum) {
        if (row.rgNo) {
          if (row.doPiece) {
            return {
              color: green[500],
              title: "La transaction a payé le document de vente",
            };
          }
          return {
            color: green[500],
            title: "La transaction a été ajoutée au compte de l'utilisateur",
          };
        }
        return {
          color: red[700],
          title: "La transaction n'a pas été correctement importée dans Sage",
        };
      }
      if (row.new) {
        return {
          color: yellow[700],
          title: "Aucun client n'a été trouvé",
        };
      }
      return {
        color: grey[500],
        title: "Veuillez contacter l'administrateur",
      };
    }; // Dans votre composant JSX :
    const { color, title } = getStatusConfig(bankTransaction);

    return (
      <Box>
        {bankTransaction.status}
        {bankTransaction.side === "debit" ? (
          <>
            <Tooltip title="Les débits ne sont pas importés automatiquement dans Sage">
              <CircleIcon
                sx={{
                  color: theme.palette.primary.main,
                }}
              />
            </Tooltip>
          </>
        ) : (
          <Box>
            <Tooltip title={title}>
              <CircleIcon sx={{ color }} />
            </Tooltip>
            {bankTransaction.new && (
              <>
                <LoadingButton
                  variant="contained"
                  loading={loading}
                  onClick={apply}
                >
                  Marquer comme effectuée
                </LoadingButton>
              </>
            )}
          </Box>
        )}
      </Box>
    );
  }
);

const QontoComponent: React.FC = React.memo(() => {
  const { t, i18n } = useTranslation();
  const refreshPage = useAppSelector(
    (state: RootState) => state.globalState.refreshPage
  );
  const [searchParams, setSearchParams] = useSearchParams();
  const dispatch = useAppDispatch();
  const [init, setInit] = React.useState(false);
  const token = useAppSelector((state: RootState) => state.globalState.token);
  const user = useAppSelector((state: RootState) => state.globalState.user);
  const [defaultItemsPerPage] = React.useState(50);
  const [bankTransfers, setBankTransfers] = React.useState<
    BankTransaction[] | undefined
  >(undefined);
  const [totalItems, setTotalItems] = React.useState(0);
  const getColumns = React.useCallback((): GridColDef[] => {
    return [
      {
        field: "id",
        headerName: t("column.id"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
      },
      {
        field: "ctNum",
        headerName: t("column.userIdentifier"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <QontoCtNumComponent bankTransaction={params.row} />
        ),
      },
      {
        field: "doPiece",
        headerName: t("column.doPiece"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <QontoFDocenteteComponent bankTransaction={params.row} />
        ),
      },
      {
        field: "amount",
        headerName: t("column.amount"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
        renderCell: (params: GridRenderCellParams) => (
          <>{priceFormat(params.row.amount, i18n.language, "EUR")}</>
        ),
      },
      {
        field: "reference",
        headerName: t("column.reference"),
        flex: 1,
        headerClassName: "background-nove",
        filterable: false,
        cellClassName: "flex-wrap-wrap",
      },
      {
        field: "side",
        headerName: t("column.side"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
      },
      {
        field: "status",
        headerName: t("column.status"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
        renderCell: (params: GridRenderCellParams) => {
          return <QontoStatusComponent bankTransaction={params.row} />;
        },
      },
      {
        field: "emittedAt",
        headerName: t("column.emittedAt"),
        flex: 0,
        headerClassName: "background-nove",
        filterable: false,
        renderCell: (params: GridRenderCellParams) =>
          new Date(params.row.emittedAt).toLocaleString(i18n.language, {
            dateStyle: "long",
            timeStyle: "medium",
            timeZone: "America/Cuiaba",
          }),
      },
    ];
  }, [i18n.language, t]);
  const [columns] = React.useState<GridColDef[]>(getColumns());

  const load = React.useCallback(
    async (force: boolean = false) => {
      setInit(true);
      if (!user || (bankTransfers !== undefined && !force)) {
        dispatch(set({ refreshPage: false }));
        return;
      }
      const searchParamsObject = searchParamToObject(searchParams);
      let hasChanged = false;
      if (
        !searchParamsObject.hasOwnProperty("page") ||
        searchParamsObject.page === "undefined"
      ) {
        searchParamsObject.page = 1;
        hasChanged = true;
      }
      if (!searchParamsObject.hasOwnProperty("itemsPerPage")) {
        searchParamsObject.itemsPerPage = defaultItemsPerPage;
        hasChanged = true;
      }
      if (hasChanged) {
        dispatch(set({ refreshPage: false }));
        setSearchParams(searchParamsObject, {
          replace: true,
        });
        return;
      }
      const response = await requestApi({
        method: GET,
        path: BANK_TRANSACTION_URL + objectToQuery(searchParamsObject),
        allowError: false,
        token: token,
        paginate: true,
      });
      if (response.statusCode === 200) {
        let maxId = Math.max(
          ...response.content.bankTransfers.map((o: BankTransaction) => o.id)
        );
        const thisBankTransfers = [
          ...Array.from(
            Array(
              Number(searchParamsObject.itemsPerPage) *
                (Number(searchParamsObject.page) - 1)
            )
          ).map(() => {
            maxId++;
            return {
              id: maxId,
            };
          }),
          ...response.content.bankTransfers,
        ];
        setTotalItems(response.content.totalItems);
        setBankTransfers(thisBankTransfers);
        dispatch(set({ nbNewQonto: response.content.nbNewQonto }));
      } else if (response.statusCode === 401) {
        toastr.info(t("word.info"), t("error.reconnect"));
      } else {
        toastr.error(t("word.error"), t("error.tryAgain"));
      }
      dispatch(set({ refreshPage: false }));
    },
    [
      defaultItemsPerPage,
      dispatch,
      bankTransfers,
      searchParams,
      setSearchParams,
      t,
      token,
      user,
    ]
  );

  const onSortModelChange = React.useCallback(
    (model: GridSortModel) => {
      if (!init) {
        return;
      }
      const searchParamsObject = searchParamToObject(searchParams);
      for (const [key] of Object.entries(searchParamsObject)) {
        if (key.startsWith("order")) {
          delete searchParamsObject[key];
        }
      }
      for (const sort of model) {
        // @ts-ignore
        searchParamsObject["order[" + sort.field + "]"] = sort.sort;
      }
      setSearchParams(searchParamsObject, {
        replace: true,
      });
    },
    [init, searchParams, setSearchParams]
  );

  const onPageSizeChange = React.useCallback(
    (pageSize: number) => {
      if (!init) {
        return;
      }
      setBankTransfers(undefined);
      const searchParamsObject = searchParamToObject(searchParams);
      searchParamsObject.itemsPerPage = pageSize;
      searchParamsObject.page = 1;
      setSearchParams(searchParamsObject, {
        replace: true,
      });
    },
    [init, searchParams, setSearchParams]
  );

  const onPageChange = React.useCallback(
    (page: number) => {
      if (!init) {
        return;
      }
      setBankTransfers(undefined);
      const searchParamsObject = searchParamToObject(searchParams);
      searchParamsObject.page = page + 1;
      setSearchParams(searchParamsObject, {
        replace: true,
      });
    },
    [init, searchParams, setSearchParams]
  );

  const getSortModel = React.useCallback(() => {
    const searchParamsObject = searchParamToObject(searchParams);
    const result: any[] = [];
    for (const [key, value] of Object.entries(searchParamsObject)) {
      if (key.startsWith("order")) {
        let field: string[] | string = key.split("[");
        field = field[1].replace("]", "");
        result.push({
          field: field,
          sort: value,
        });
      }
    }
    return result;
  }, [searchParams]);

  React.useEffect(() => {
    load();
  }, []); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (init && refreshPage) {
      load(true);
    }
  }, [refreshPage]); // eslint-disable-line react-hooks/exhaustive-deps

  React.useEffect(() => {
    if (init && !refreshPage) {
      dispatch(set({ refreshPage: true }));
    }
  }, [searchParams]); // eslint-disable-line react-hooks/exhaustive-deps

  const searchParamsObject = searchParamToObject(searchParams);
  return (
    <>
      <FilterSearchComponent
        init={
          init && searchParamsObject.itemsPerPage && searchParamsObject.page
        }
        placeholder="word.searchPaymentPlaceholder"
      />
      {init && searchParamsObject.itemsPerPage && searchParamsObject.page && (
        // https://mui.com/x/react-data-grid/components/#pagination
        <DataGrid
          initialState={{
            sorting: {
              sortModel: getSortModel(),
            },
          }}
          loading={refreshPage}
          getRowHeight={() => "auto"}
          rows={refreshPage ? [] : bankTransfers ?? []}
          onSortModelChange={onSortModelChange}
          sortingMode="server"
          page={Number(searchParamsObject.page) - 1}
          rowsPerPageOptions={[10, 25, 50]}
          pageSize={Number(searchParamsObject.itemsPerPage)}
          onPageSizeChange={onPageSizeChange}
          onPageChange={onPageChange}
          rowCount={totalItems}
          columns={columns}
          autoHeight={true}
          disableExtendRowFullWidth={true}
          localeText={getLocaleDataGrid(i18n.language)}
        />
      )}
    </>
  );
});

export default QontoComponent;
